import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DemoClockView, DemoMoment, DemoSessionView, DemoSurface, PlatformDemoEntryLink } from "@verity-connect/contracts";
import { DEMO_SURFACES, demoSurfaceFromPath } from "./demo-surfaces";
import { VerityConnectBrand } from "./VerityConnectBrand";

const DEMO_CLOCK_KEY = "connect:demo-clock";
const DEMO_CLOCK_EVENT = "connect:demo-clock-changed";

export function DemoEntry() {
  const [message, setMessage] = useState("Preparing your private demo…");
  const redeeming = useRef(false);

  useEffect(() => {
    if (redeeming.current) return;
    redeeming.current = true;
    const token = new URLSearchParams(window.location.hash.replace(/^#/u, "")).get("t") ?? "";
    window.history.replaceState(null, "", window.location.pathname);
    if (!token) { setMessage("This demo link is incomplete. Open it again from Verity Connect."); return; }
    void fetch("/api/demo/redeem", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    }).then(async (response) => {
      const body = await response.json().catch(() => null) as { path?: string; message?: string } | null;
      if (!response.ok || !body?.path) throw new Error(body?.message ?? "This demo could not be opened.");
      sessionStorage.setItem("connect:demo-workspace", "active");
      await loadDemoClock();
      window.location.replace(body.path);
    }).catch((error: unknown) => setMessage(error instanceof Error ? error.message : "This demo could not be opened."));
  }, []);

  return <main className="demo-entry"><VerityConnectBrand className="demo-entry-brand" inverse /><h1>{message}</h1><span>Demo work stays separate from every live association.</span></main>;
}

export function DemoWorkspace({ pathname, children }: { pathname: string; children: ReactNode }) {
  const surface = demoSurfaceFromPath(pathname);
  return <div className={`demo-stage demo-stage-${surface}`}>
    <DemoBar pathname={pathname} />
    {surface === "attendee" ? <main className="demo-phone-stage" aria-label="Attendee app preview">
      <div className="demo-phone" aria-label="Large iPhone preview">
        <span className="demo-phone-speaker" aria-hidden="true" />
        <iframe src={`${pathname}${window.location.search}`} title="WVCEA attendee app" />
      </div>
    </main> : children}
  </div>;
}

export function isInsideDemoPhone(): boolean {
  if (!isDemoHost() || window.self === window.top || !document.referrer) return false;
  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

const SURFACE_WORDS: Record<DemoSurface, string> = {
  attendee: "attendee",
  organizer: "association staff",
  presenter: "presenter",
  school: "school representative",
  exhibitor: "exhibitor",
};

export function DemoBar({ pathname }: { pathname: string }) {
  const surface = demoSurfaceFromPath(pathname);
  const [session, setSession] = useState<DemoSessionView | null>(null);
  const [busy, setBusy] = useState<DemoSurface | "reset" | "end" | "moment" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moment, setMoment] = useState<DemoMoment>("live");
  const [controlsOpen, setControlsOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const load = () => void fetch(`/api/demo/session?surface=${surface}`, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("This demo has ended.");
        const next = await response.json() as DemoSessionView;
        setSession(next);
        setMoment(next.moment);
        if (next.clock) cacheDemoClock(next.clock);
      })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : "This demo has ended."); });
    load();
    const timer = window.setInterval(load, 30_000);
    return () => { window.clearInterval(timer); controller.abort(); };
  }, [surface]);

  const openSurface = async (next: DemoSurface) => {
    if (busy) return;
    setBusy(next); setError(null);
    try {
      const response = await fetch("/api/demo/surface", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ surface: next }) });
      const body = await response.json().catch(() => null) as { path?: string; message?: string } | null;
      if (!response.ok || !body?.path) throw new Error(body?.message ?? "That surface could not be opened.");
      window.location.assign(body.path);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "That surface could not be opened."); setBusy(null); }
  };
  const changeMoment = async (next: DemoMoment) => {
    if (busy) return;
    setBusy("moment"); setError(null);
    try {
      const response = await fetch("/api/demo/moment", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ moment: next }),
      });
      if (!response.ok) throw new Error("The convention moment could not be changed.");
      setMoment(next);
      // The new moment anchors a new hour, and the reload below must not open on the
      // old one.
      await loadDemoClock();
      window.location.reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The convention moment could not be changed.");
      setBusy(null);
    }
  };
  const reset = async () => {
    if (busy || !window.confirm("Reset this demo? All changes in it will be discarded.")) return;
    setBusy("reset"); setError(null);
    try {
      const response = await fetch("/api/demo/reset", { method: "POST", credentials: "same-origin" });
      const body = await response.json().catch(() => null) as PlatformDemoEntryLink | { message?: string } | null;
      if (!response.ok || !body || !("url" in body)) throw new Error(body && "message" in body ? body.message : "The demo could not be reset.");
      sessionStorage.removeItem(DEMO_CLOCK_KEY);
      window.location.replace(body.url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "The demo could not be reset."); setBusy(null); }
  };
  const end = async () => {
    if (busy || !window.confirm("End this demo and discard its changes?")) return;
    setBusy("end"); setError(null);
    try {
      const response = await fetch("/api/demo/end", { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error("The demo could not be ended.");
      window.location.assign(session?.platformUrl ?? "https://platform.verityconnect.app/demos");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "The demo could not be ended."); setBusy(null); }
  };

  return <aside className={`demo-rail${controlsOpen ? " is-open" : ""}`} aria-label="Demo workspace controls">
    <div className="demo-rail-status"><VerityConnectBrand inverse companyLine={false} /><div><strong>Private demo</strong><small>{session ? "Runs until you reset or end it" : "Loading…"} · Nothing leaves this demo</small></div></div>
    <button className="demo-rail-toggle" type="button" aria-expanded={controlsOpen} onClick={() => setControlsOpen((value) => !value)}>{controlsOpen ? "Hide" : "Controls"}</button>
    <label className="demo-rail-moment"><span>Show</span><select value={moment} disabled={Boolean(busy)} onChange={(event) => void changeMoment(event.target.value as DemoMoment)}><option value="before">Before convention</option><option value="live">Convention underway</option><option value="after">After convention</option></select></label>
    <nav aria-label="Demo surfaces">{DEMO_SURFACES.map((item) => <button type="button" key={item.id} className={surface === item.id ? "is-current" : undefined} disabled={Boolean(busy)} onClick={() => void openSurface(item.id)}><strong>{item.label}</strong><small>{busy === item.id ? "Opening…" : item.detail}</small></button>)}</nav>
    {/* Above the captured activity, because signing in comes before anything a demo
        captures — and it is the first thing somebody being shown this will want to try. */}
    {session?.signIn && <details className="demo-rail-signin"><summary>Sign in as someone <b>{session.signIn.people.length}</b></summary><div>
      <p>Use these on the real sign-in screen. They work only inside this demo.</p>
      {/* Above the list, not below it. One password covers all five, and the list scrolls —
          the value somebody needs first should not be the one they have to scroll for. */}
      <p className="demo-rail-signin-password">Password for all of them<code>{session.signIn.password}</code></p>
      <dl>
        {session.signIn.people.map((person) => <div key={person.kind}>
          <dt>{person.displayName}<span>{SURFACE_WORDS[person.kind]}</span></dt>
          <dd><code>{person.email}</code></dd>
        </div>)}
      </dl>
    </div></details>}
    <details className="demo-rail-activity"><summary>Captured activity <b>{session?.workspace.capturedActions.length ?? 0}</b></summary><div>{session?.workspace.capturedActions.length ? session.workspace.capturedActions.map((item) => <article key={item.id}><span>{item.channel}</span><strong>{item.subject}</strong><small>{item.recipient ?? "Kept inside the demo"}</small></article>) : <p>Messages and outside actions will appear here instead of being sent.</p>}</div></details>
    <div className="demo-rail-actions"><button type="button" disabled={Boolean(busy)} onClick={() => void reset()}>{busy === "reset" ? "Resetting…" : "Reset"}</button><button type="button" disabled={Boolean(busy)} onClick={() => void end()}>{busy === "end" ? "Ending…" : "End demo"}</button></div>
    {error && <p className="demo-rail-error" role="alert">{error} <a href="https://platform.verityconnect.app/demos">Return to Verity Connect</a></p>}
  </aside>;
}

export function isDemoHost(): boolean {
  return window.location.hostname === "demo.verityconnect.app" || window.location.hostname.startsWith("demo.")
    || (import.meta.env.DEV && sessionStorage.getItem("connect:demo-workspace") === "active");
}

/**
 * The convention's own clock, for any surface inside a demo.
 *
 * The server anchors the hour and hands back its distance from the real clock; a
 * surface adds that distance to its own ticking clock. Both halves matter. The server
 * owning it keeps the attendee's home page and the organizer's command centre from
 * disagreeing about whether a session is running, and the ticking is what makes
 * "happening now" move during a demonstration — it used to be a fixed instant, so the
 * next session never arrived however long a room watched.
 */
export function useDemoClock(): DemoClockView | null {
  const [clock, setClock] = useState<DemoClockView | null>(() => isDemoHost() ? cachedDemoClock() : null);
  useEffect(() => {
    if (!isDemoHost()) return;
    void loadDemoClock().then((value) => { if (value) setClock(value); });
    const read = () => setClock(cachedDemoClock());
    window.addEventListener(DEMO_CLOCK_EVENT, read);
    return () => window.removeEventListener(DEMO_CLOCK_EVENT, read);
  }, []);
  return clock;
}

export function demoExperienceNow(clock: DemoClockView | null, actualNow: Date): Date {
  return clock ? new Date(actualNow.getTime() + clock.offsetMs) : actualNow;
}

export async function loadDemoClock(): Promise<DemoClockView | null> {
  try {
    const response = await fetch("/api/demo/clock", { credentials: "same-origin" });
    if (!response.ok) return null;
    const clock = await response.json() as DemoClockView | null;
    if (clock) cacheDemoClock(clock);
    return clock;
  } catch {
    // A demo that cannot reach its own clock falls back to the real one rather than
    // showing nothing.
    return null;
  }
}

export function cacheDemoClock(clock: DemoClockView): void {
  // The rail asks for the demo's state every few seconds and the answer is usually the
  // same clock. Announcing it anyway would redraw every screen on that rhythm, so only a
  // real change is worth telling anybody about.
  const current = cachedDemoClock();
  const changed = !current || current.moment !== clock.moment
    || Math.abs(current.offsetMs - clock.offsetMs) > 1_000;
  try {
    sessionStorage.setItem(DEMO_CLOCK_KEY, JSON.stringify(clock));
    if (changed) window.dispatchEvent(new Event(DEMO_CLOCK_EVENT));
  } catch { /* The clock still works for this page when storage is unavailable. */ }
}

export function cachedDemoClock(): DemoClockView | null {
  try {
    const value = sessionStorage.getItem(DEMO_CLOCK_KEY);
    if (!value) return null;
    const clock = JSON.parse(value) as DemoClockView;
    return typeof clock?.offsetMs === "number" && typeof clock.moment === "string" ? clock : null;
  } catch {
    return null;
  }
}
